/**
 * Dialog primitive -- Leviathan design system.
 *
 * DialogContent uses tier4 glass surface for the modal backdrop and popup.
 * The enter animation composes the dialogEnter motion preset.
 *
 * API is preserved: Dialog, DialogTrigger, DialogPortal, DialogClose,
 * DialogOverlay, DialogContent, DialogHeader, DialogFooter,
 * DialogTitle, DialogDescription.
 *
 * Do not add em-dashes anywhere.
 */

import * as React from 'react'
import { Dialog as DialogPrimitive } from '@base-ui/react/dialog'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { XIcon } from 'lucide-react'
import { elevation } from '@/design-system/tokens'
import { dialogEnter, reducedMotion as stripMotion } from '@/design-system/motion'

function Dialog({ ...props }: DialogPrimitive.Root.Props) {
  return <DialogPrimitive.Root data-slot="dialog" {...props} />
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />
}

function DialogOverlay({ className, ...props }: DialogPrimitive.Backdrop.Props) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'fixed inset-0 isolate z-50 duration-100',
        'data-open:animate-in data-open:fade-in-0 data-closed:animate-out data-closed:fade-out-0',
        className,
      )}
      style={{
        background: 'rgba(0, 0, 0, 0.55)',
        backdropFilter: 'blur(2px)',
        WebkitBackdropFilter: 'blur(2px)',
      }}
      {...props}
    />
  )
}

const tier4 = elevation.tier4

function DialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: DialogPrimitive.Popup.Props & {
  showCloseButton?: boolean
}) {
  const shouldReduceMotion = useReducedMotion()
  const motionPreset = shouldReduceMotion ? stripMotion(dialogEnter) : dialogEnter

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Popup
        data-slot="dialog-content"
        render={({ ref, ...renderProps }) => {
          // framer-motion's HTMLMotionProps clashes with React HTML drag events.
          // Cast through unknown so TS accepts the spread onto motion.div.
          const motionProps = renderProps as unknown as React.ComponentProps<typeof motion.div>
          return (
            <motion.div
              ref={ref}
              {...motionPreset}
              {...motionProps}
            />
          )
        }}
        className={cn(
          'fixed top-1/2 left-1/2 z-50 grid w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 rounded-xl p-4 text-sm text-foreground outline-none sm:max-w-sm',
          className,
        )}
        style={{
          background: tier4.background,
          border: tier4.border,
          backdropFilter: tier4.backdropFilter,
          WebkitBackdropFilter: tier4.backdropFilter,
          boxShadow: '0 32px 80px -20px rgba(0, 0, 0, 0.8)',
        }}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            data-slot="dialog-close"
            render={
              <Button
                variant="ghost"
                className="absolute top-2 right-2"
                size="icon-sm"
              />
            }
          >
            <XIcon />
            <span className="sr-only">Close</span>
          </DialogPrimitive.Close>
        )}
      </DialogPrimitive.Popup>
    </DialogPortal>
  )
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('flex flex-col gap-2', className)}
      {...props}
    />
  )
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        '-mx-4 -mb-4 flex flex-col-reverse gap-2 rounded-b-xl border-t border-white/6 bg-white/3 p-4 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close render={<Button variant="outline" />}>
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  )
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('font-heading text-base leading-none font-medium', className)}
      {...props}
    />
  )
}

function DialogDescription({ className, ...props }: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-sm text-muted-foreground *:[a]:underline *:[a]:underline-offset-3 *:[a]:hover:text-foreground',
        className,
      )}
      {...props}
    />
  )
}

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
}
